/**
 * previousDoseCascade.js
 * When a previous-vaccination dose date changes, auto-uncheck subsequent doses
 * on the same vaccine that are no longer selectable or have invalid dates.
 */

import dayjs from 'dayjs';
import { getPWPreviousDoseStatus } from '../scenarios/pregnant-women-previous';
import { getChildPreviousDoseStatus } from '../scenarios/children-previous';

export const buildSelectedDosesFromAvailable = (availableDoses) =>
  (availableDoses || []).map((dose) => ({
    selected: dose.selected || false,
    administeredDate: dose.administeredDate || null,
  }));

export const getPreviousDoseEligibility = (
  vaccine,
  doseIndex,
  beneficiaryType,
  profileData,
  administeredVaccinations,
  decryptFunction,
  selectedDoses,
  identityContext
) => {
  if (beneficiaryType === 'PW') {
    return getPWPreviousDoseStatus(
      vaccine,
      doseIndex,
      profileData,
      decryptFunction,
      { doses: selectedDoses },
      identityContext
    );
  }

  return getChildPreviousDoseStatus(
    vaccine,
    doseIndex,
    profileData?.dob,
    administeredVaccinations,
    decryptFunction,
    selectedDoses,
    [],
    identityContext
  );
};

export const isPreviousDoseSelectable = (doseStatus) =>
  doseStatus?.checkboxEnabled === true && doseStatus?.status === 'enabled';

export const isDateWithinDoseWindow = (date, doseStatus) => {
  if (!date) {
    return true;
  }

  if (!doseStatus?.minDate || !doseStatus?.maxDate) {
    return false;
  }

  const entered = dayjs(date);
  return (
    !entered.isBefore(dayjs(doseStatus.minDate), 'day') &&
    !entered.isAfter(dayjs(doseStatus.maxDate), 'day')
  );
};

/**
 * Uncheck and clear dates for subsequent doses that become ineligible after
 * a date change on changedDoseIndex (or any earlier dose in the chain).
 *
 * @returns {{ doses: Array, clearedDoseNames: string[] }}
 */
export const cascadeUncheckIneligibleSubsequentDoses = (
  vaccine,
  doses,
  changedDoseIndex,
  beneficiaryType,
  profileData,
  administeredVaccinations,
  decryptFunction,
  identityContext
) => {
  if (!doses || doses.length <= 1 || changedDoseIndex >= doses.length - 1) {
    return { doses, clearedDoseNames: [] };
  }

  const workingDoses = doses.map((dose) => ({ ...dose }));
  const clearedDoseNames = [];

  for (let i = changedDoseIndex + 1; i < workingDoses.length; i++) {
    if (!workingDoses[i].selected) {
      continue;
    }

    const selectedDoses = buildSelectedDosesFromAvailable(workingDoses);
    const vaccineWithDoses = { ...vaccine, availableDoses: workingDoses };
    const doseStatus = getPreviousDoseEligibility(
      vaccineWithDoses,
      i,
      beneficiaryType,
      profileData,
      administeredVaccinations,
      decryptFunction,
      selectedDoses,
      identityContext
    );

    const dateInvalid =
      workingDoses[i].administeredDate &&
      !isDateWithinDoseWindow(workingDoses[i].administeredDate, doseStatus);

    if (!isPreviousDoseSelectable(doseStatus) || dateInvalid) {
      const doseName = workingDoses[i].doseName || `Dose ${i + 1}`;
      clearedDoseNames.push(doseName);
      workingDoses[i] = {
        ...workingDoses[i],
        selected: false,
        administeredDate: null,
      };
    }
  }

  return { doses: workingDoses, clearedDoseNames };
};
